import React from 'react';
import DropTarget from '@uppy/drop-target';
import ReactDomAdapter from '../ReactDomAdapter';
import Logger from '../Logger';
import UiMutator from '../UiMutator';
import { UploaderModal, ImageEditorModal, UppyClient, ProviderConfig } from '../components';

/**
 * KjUploader core
 * Intended to be a drop-in replacement for App.FilepickerUpload in the monolith.
 *
 * Ideally this can be used in the following ways:
 * - Within a useKjUploader hook that is distributed by this package
 * - Within KjUploaderComponent via useKjUploader hook
 * - As a drop-in replacement for App.Filepicker
 *
 * This structure allows us to centralize the uploader logic in 1 location
 * rather than having multiple implementations of interacting with the service.
 * (Hence the 'Core' naming of this class.)
 *
 * This class would ReactDOM-mount a React component to a provided DOM node
 * provided within the config.
 *
 */

const MOUNT_NODE_ID = 'kj-uploader';

class KjUploader {
  constructor(config) {
    // TODO: Argument check `config`

    this.config = config;
    this.logger = new Logger({
      datadogClientToken: this.config.datadogClientToken,
      context: this.config.loggerContext,
      enableConsole: this.config.debug,
    });
    this.uiMutator = new UiMutator({ el: this.#mountNode(), config });
    this.logger.debug('new KjUploader', this.config);
    this.dndUppyClient = null;
  }

  open() {
    ReactDomAdapter.render(<UploaderModal {...this.#componentProps()} />, this.#mountNode());

    this.logger.debug('open');
    this.uiMutator.observe();
    this.config.onOpen?.();
  }

  close() {
    this.uiMutator.disconnect();
    ReactDomAdapter.unmount(this.#mountNode());

    this.logger.debug('close');
    this.config.onClose?.();
  }

  storeUrl(url) {
    const options = {
      ...this.#componentProps(),
      maxFiles: 1,
      autoProceed: false,
      sources: ['Url']
    };

    const uppy = UppyClient(
      options,
      ProviderConfig(options).plugins
    );

    uppy.on('file-added', (file) => {
      const imageEditor = uppy.getPlugin('ImageEditor');

      if (options.autoOpenFileEditor && imageEditor?.canEditFile?.(file)) {
        this.#mountImageEditorModal({ uppy, file, options });
      } else {
        uppy.upload();
      }
    });

    uppy.getPlugin('Url').addFile(url);
  }

  makeDropPane({ target, onDragOver, onDrop, onDragLeave }) {
    const options = {
      ...this.#componentProps(),
      autoProceed: true,
      autoOpenFileEditor: false,
      sources: []
    };

    if (!this.dndUppyClient) {
      this.dndUppyClient = UppyClient(options);
    }

    this.removeDropPane(); // make sure there is only one DropTarget plugin.
    this.dndUppyClient.use(DropTarget, { target, onDragOver, onDrop, onDragLeave });
  }

  removeDropPane() {
    if (this.dndUppyClient) {
      const dropTargetPlugin = this.dndUppyClient.getPlugin('DropTarget');
      if (dropTargetPlugin) {
        this.dndUppyClient.removePlugin(dropTargetPlugin);
      }
    }
  }

  #componentProps() {
    return {
      ...this.config,
      logger: this.logger,
      unmountReact: this.close.bind(this),
    };
  }

  #mountNode() {
    return document.getElementById(MOUNT_NODE_ID) || this.#createMountNode();
  }

  #createMountNode() {
    const el = document.createElement('div');
    el.setAttribute('id', MOUNT_NODE_ID);
    return this.config.parentNode.appendChild(el);
  }

  #mountImageEditorModal({ uppy, file, options }) {
    ReactDomAdapter.render(
      <ImageEditorModal
        {...options}
        uppy={uppy}
        fileId={file.id}
        modalTitle="Edit image"
      />,
      this.#mountNode()
    );
    this.logger.debug('open image editor');
    this.config.onOpen?.();
  }
}

export default KjUploader;
